import { useState, useEffect, useCallback } from 'react';
import { ApiService } from '../services/api';
import { OrganizationFilters, DashboardStats, MtlsOrganization, MtlsProduct } from '../types/mtls';

// Custom hook for mTLS organizations
export const useMtlsOrganizations = (filters?: OrganizationFilters) => {
  const [organizations, setOrganizations] = useState<MtlsOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
  });

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.listMtlsOrganizations(
        filters?.page,
        filters?.size,
        filters?.search // This maps to 'name' parameter
      );
      setOrganizations(response.data || []);
      setPagination({
        page: response.pagination?.page || filters?.page || 1,
        size: response.pagination?.limit || filters?.size || 10,
        total: response.pagination?.total || 0,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load organizations');
      setOrganizations([]);
      setPagination({ page: 1, size: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  return { organizations, loading, error, pagination, reload: loadOrganizations };
};

// Custom hook for mTLS products with filtering and pagination
export const useMtlsProducts = (filters?: {
  page?: number;
  size?: number;
  name?: string;
  organizationId?: number;
}) => {
  const [products, setProducts] = useState<MtlsProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
  });

  // Extract individual filter values to prevent object reference issues
  const page = filters?.page;
  const size = filters?.size;
  const name = filters?.name;
  const organizationId = filters?.organizationId;

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.listMtlsProducts(
        page,
        size,
        name,
        organizationId
      );
      setProducts(response.data || []);
      setPagination({
        page: response.pagination?.page || page || 1,
        size: response.pagination?.limit || size || 10,
        total: response.pagination?.total || 0,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
      setProducts([]);
      setPagination({ page: 1, size: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, size, name, organizationId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return { products, loading, error, pagination, reload: loadProducts };
};

// Temporary stub for mTLS certificates (organization-focused for now)
export const useMtlsCertificates = (
  organizationId?: number, 
  status?: 'active' | 'expired' | 'revoked', 
  certificateType?: "prod" | "non-prod"
) => {
  const [certificates, setCertificates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    total: 0,
  });

  const loadCertificates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!organizationId) {
        setCertificates([]);
        setPagination({ page: 1, size: 10, total: 0 });
        return;
      }
      const response = await ApiService.listMtlsOrganizationCertificates(organizationId);
      setCertificates(response.certificates || []);
      setPagination({ page: 1, size: 10, total: 0 });
    } catch (err: any) {
      setError(err.message || 'Failed to load certificates');
      setCertificates([]);
      setPagination({ page: 1, size: 10, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [organizationId, status, certificateType]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  return { certificates, loading, error, pagination, reload: loadCertificates };
};

// Custom hook for mTLS dashboard statistics
export const useMtlsDashboardStats = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalCertificates: 0,
    activeCertificates: 0,
    expiringCertificates: 0,
    totalOrganizations: 0,
    totalProducts: 0,
    totalEnvironments: 0,
    deploymentStatus: {
      successful: 0,
      failed: 0,
      pending: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getMtlsDashboardStats();
      setStats(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard statistics');
      // Keep default stats on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, reload: loadStats };
};